import { User } from '@app/components/various/Avatar';
import { Dropdown, DropdownMenu, DropdownTrigger } from '@app/components/various/Dropdown';
import { Account, Language, Logout } from '@app/components/various/Icons';
import { logoutRequest as logout, requestLanguageChange } from '@app/ducks';
import { paths } from '@app/paths';
import { useAmplitude, useTranslationsConfig } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { renderSentryMaskedText } from '@app/utils/monitoring';
import { Translations, User as UserType } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

interface Props {
  user: UserType;
  companyName: string;
}

export const AccountDropdown = ({ user, companyName }: Props) => {
  const dispatch = useDispatch();
  const { currentLanguageCode, availableLanguages, areMultipleLanguagesAvailable } = useTranslationsConfig();
  const { t } = useTranslation(['common', 'accounts', 'login']);
  const { trackEvent } = useAmplitude();

  const handleLogout = React.useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const changeLanguage = React.useCallback(
    (language: Translations.SupportedLanguagesCodes) => {
      dispatch(requestLanguageChange(language));
      trackEvent({
        name: 'userPreferences.button.language.click',
        properties: {
          deviceLanguage: navigator.language,
          path: window.location.pathname,
          previousLanguage: currentLanguageCode,
          selectedLanguage: language,
        },
      });
    },
    [currentLanguageCode, dispatch, trackEvent],
  );

  const languageMenuItem = {
    description: availableLanguages.find(lang => lang.code === currentLanguageCode)?.label,
    icon: Language,
    key: 'language',
    label: t('common:language_one'),
    menu: {
      items: availableLanguages.map(lang => ({ key: lang.code, label: lang.label })),
      onSelectionChange: ([language]: Translations.SupportedLanguagesCodes[]) => {
        if (isDefined(language)) {
          changeLanguage(language);
        }
      },
      selectedKeys: [currentLanguageCode],
      selectionMode: 'single' as const,
    },
  };

  const menuItems = [
    {
      description: !isEmpty(user.name) ? renderSentryMaskedText(user.name) : undefined,
      icon: Account,
      key: 'account',
      label: t('accounts:my_account'),
      to: paths.ACCOUNT,
    },
    ...(areMultipleLanguagesAvailable ? [languageMenuItem] : []),
    {
      hasDivider: true,
      icon: Logout,
      key: 'logout',
      label: t('login:log_out'),
      onClick: handleLogout,
    },
  ];

  return (
    <Dropdown title={renderSentryMaskedText(user.name)}>
      <DropdownTrigger>
        <button type="button">
          <User user={user} companyName={companyName} />
        </button>
      </DropdownTrigger>
      <DropdownMenu items={menuItems} />
    </Dropdown>
  );
};
